import type { AudioFrame, AudioSourceKind } from '../types'

/**
 * Audio engine based on the Web Audio API.
 *
 * Supports three sources:
 *  - 'file'     : a local audio file (mp3/wav/...) played by the app.
 *  - 'loopback' : system or tab audio via getDisplayMedia({audio:true}).
 *                 It's the only way in the browser to react to Spotify in real time.
 *  - 'mic'      : microphone (getUserMedia), useful for testing.
 *
 * Exposes an AnalyserNode from which frequencies and waveform are read each frame,
 * plus bands (bass/mid/treble), global level, and beat detection.
 */
export class AudioEngine {
  private ctx: AudioContext | null = null
  private analyser: AnalyserNode | null = null
  private source: AudioNode | null = null
  // Audible volume is a GainNode AFTER the analyser tap, so the spectrum always
  // sees full-amplitude audio — visuals react the same even at volume 0.
  private gainNode: GainNode | null = null
  private mediaStream: MediaStream | null = null
  // Audio tap for video export (a MediaStreamDestination fed by the source).
  private captureDest: MediaStreamAudioDestinationNode | null = null
  private audioEl: HTMLAudioElement | null = null
  private volume = 1
  private loop = true
  /** URL of the file currently loaded (null for loopback/mic/none). Lets callers
   *  tell whose song is in the shared engine (Live vs Studio). */
  currentUrl: string | null = null
  /** Called when a (non-looping) track finishes — used to auto-advance. */
  onEnded: (() => void) | null = null

  private freqBytes: Uint8Array = new Uint8Array(0)
  private timeBytes: Uint8Array = new Uint8Array(0)
  private freqFloat: Float32Array = new Float32Array(0)
  private waveFloat: Float32Array = new Float32Array(0)

  // Beat detection state (based on bass energy).
  private bassEnergyAvg = 0
  private beatCooldown = 0
  private beatIntensity = 0

  // Tempo (BPM) estimation: timestamps of recent beats + smoothed BPM.
  private bpmClock = 0
  private beatTimes: number[] = []
  /** Estimated tempo of the current track (beats per minute, smoothed). */
  bpm = 120
  // Beat/BPM are analyzed ONCE per frame and shared (the engine has several
  // readers: the Scene, the audio meters…). Avoids double-counting the clock.
  private lastAnalyzeMs = 0
  private frameBeat = false

  kind: AudioSourceKind = 'none'

  private get fftSize() {
    return 2048
  }

  private ensureContext() {
    if (!this.ctx) {
      this.ctx = new AudioContext()
    }
    if (this.ctx.state === 'suspended') {
      void this.ctx.resume()
    }
    return this.ctx
  }

  private setupAnalyser(node: AudioNode, toDestination: boolean) {
    const ctx = this.ensureContext()
    const analyser = ctx.createAnalyser()
    analyser.fftSize = this.fftSize
    analyser.smoothingTimeConstant = 0.8
    // Tap the analyser straight off the source (full amplitude, pre-volume).
    node.connect(analyser)
    // Audible path (file playback) goes through a gain we control, so lowering
    // the heard volume never weakens the data feeding the visuals.
    if (toDestination) {
      const gain = ctx.createGain()
      gain.gain.value = this.volume
      node.connect(gain)
      gain.connect(ctx.destination)
      this.gainNode = gain
    }
    this.analyser = analyser
    this.source = node

    const bins = analyser.frequencyBinCount
    this.freqBytes = new Uint8Array(bins)
    this.freqFloat = new Float32Array(bins)
    this.timeBytes = new Uint8Array(analyser.fftSize)
    this.waveFloat = new Float32Array(analyser.fftSize)
  }

  /**
   * Attach an EXTERNAL analyser (the host editor's own audio graph) instead of
   * creating a source here. Lets read() produce frames from the song the host is
   * already playing — no second audio element, no echo. The host owns the
   * context + analyser; this engine only reads from them.
   */
  attachExternalAnalyser(ctx: AudioContext, analyser: AnalyserNode): void {
    this.ctx = ctx
    this.analyser = analyser
    this.source = null
    this.kind = 'file'
    const bins = analyser.frequencyBinCount
    this.freqBytes = new Uint8Array(bins)
    this.freqFloat = new Float32Array(bins)
    this.timeBytes = new Uint8Array(analyser.fftSize)
    this.waveFloat = new Float32Array(analyser.fftSize)
  }

  /** Loads and plays a local audio file. Returns once it starts playing. */
  async loadFile(file: File): Promise<void> {
    await this.loadUrl(URL.createObjectURL(file))
  }

  /**
   * Loads audio from a URL (e.g. the local library at /media/audio).
   * If `autoplay` is false (restoring on refresh), it leaves the audio loaded but
   * paused: the browser blocks autoplay without a user gesture, so it will
   * play on the first Play.
   */
  async loadUrl(url: string, autoplay = true): Promise<void> {
    this.disconnect()
    const ctx = this.ensureContext()
    const el = new Audio()
    el.src = url
    el.crossOrigin = 'anonymous'
    el.loop = this.loop
    // The element always runs at full level; the audible volume is the GainNode.
    el.volume = 1
    el.addEventListener('ended', () => this.onEnded?.())
    this.audioEl = el

    const node = ctx.createMediaElementSource(el)
    // node -> analyser (full) and node -> gain -> destination (audible).
    this.setupAnalyser(node, true)

    this.kind = 'file'
    this.currentUrl = url
    if (autoplay) await el.play().catch(() => {})
  }

  /**
   * Captures system or tab audio (loopback) via getDisplayMedia.
   * The user must check "share audio" in the browser dialog.
   */
  async startLoopback(): Promise<void> {
    this.disconnect()
    const ctx = this.ensureContext()
    // We request video + audio: many browsers only offer "share audio"
    // when a screen/tab is also being shared.
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: true,
      audio: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      } as MediaTrackConstraints,
    })

    if (stream.getAudioTracks().length === 0) {
      stream.getTracks().forEach((t) => t.stop())
      throw new Error(
        'No audio was shared. Try again and check the "Share system/tab audio" box.',
      )
    }
    // We don't need the video: we stop those tracks to avoid wasting resources.
    stream.getVideoTracks().forEach((t) => t.stop())

    this.mediaStream = stream
    const node = ctx.createMediaStreamSource(stream)
    // NOTE: do NOT connect to destination, to avoid echo/feedback.
    this.setupAnalyser(node, false)
    this.kind = 'loopback'
  }

  /** Captures from the microphone. */
  async startMic(): Promise<void> {
    this.disconnect()
    const ctx = this.ensureContext()
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    this.mediaStream = stream
    const node = ctx.createMediaStreamSource(stream)
    this.setupAnalyser(node, false)
    this.kind = 'mic'
  }

  play() {
    void this.ctx?.resume()
    void this.audioEl?.play()
  }

  pause() {
    this.audioEl?.pause()
  }

  /** Sets the AUDIBLE volume (0..1) via the gain node. The analyser is tapped
   *  before it, so the spectrum (and the visuals) stay at full strength even at 0. */
  setVolume(v: number) {
    this.volume = Math.max(0, Math.min(1, v))
    if (this.gainNode) this.gainNode.gain.value = this.volume
  }

  /** Whether the current track loops. When off, it fires onEnded at the end. */
  setLoop(v: boolean) {
    this.loop = v
    if (this.audioEl) this.audioEl.loop = v
  }

  /** Whether a file audio element is currently loaded. */
  get hasAudio() {
    return this.audioEl !== null
  }

  /** Current playback position (seconds) of the file audio. */
  get currentTime() {
    return this.audioEl?.currentTime ?? 0
  }

  /** Total duration (seconds) of the file audio, or 0 if unknown. */
  get duration() {
    const d = this.audioEl?.duration
    return d && isFinite(d) ? d : 0
  }

  /** Seeks the file audio to the given time (seconds). */
  seek(seconds: number) {
    if (!this.audioEl || !isFinite(seconds)) return
    const dur = this.audioEl.duration
    this.audioEl.currentTime = Math.max(0, isFinite(dur) ? Math.min(seconds, dur) : seconds)
  }

  get isPlaying() {
    if (this.kind === 'file') return !!this.audioEl && !this.audioEl.paused
    return this.kind === 'loopback' || this.kind === 'mic'
  }

  /** Returns a MediaStream carrying the current audio (for muxing into a video
   *  export). Tapped off the source pre-gain, so it's full-amplitude even if the
   *  heard volume is low. Call `stopCapture()` to detach when the export ends. */
  startCapture(): MediaStream | null {
    if (!this.ctx || !this.source) return null
    this.stopCapture()
    const dest = this.ctx.createMediaStreamDestination()
    this.source.connect(dest)
    this.captureDest = dest
    return dest.stream
  }

  stopCapture() {
    if (this.captureDest && this.source) {
      try {
        this.source.disconnect(this.captureDest)
      } catch {
        /* noop */
      }
    }
    this.captureDest = null
  }

  disconnect() {
    this.stopCapture()
    this.audioEl?.pause()
    if (this.audioEl) {
      this.audioEl.src = ''
      this.audioEl = null
    }
    this.currentUrl = null
    this.mediaStream?.getTracks().forEach((t) => t.stop())
    this.mediaStream = null
    try {
      this.source?.disconnect()
      this.analyser?.disconnect()
      this.gainNode?.disconnect()
    } catch {
      /* noop */
    }
    this.source = null
    this.analyser = null
    this.gainNode = null
    this.kind = 'none'
    // Reset tempo estimation so the next track re-estimates from scratch.
    this.beatTimes = []
    this.bpmClock = 0
    this.lastAnalyzeMs = 0
    this.frameBeat = false
  }

  /** Reads the current audio state. Call once per render frame. */
  read(dt: number): AudioFrame {
    const empty: AudioFrame = {
      frequencies: this.freqFloat,
      waveform: this.waveFloat,
      bass: 0,
      mid: 0,
      treble: 0,
      level: 0,
      beat: false,
      beatIntensity: this.beatIntensity,
      bpm: this.bpm,
    }
    if (!this.analyser) return empty

    this.analyser.getByteFrequencyData(this.freqBytes)
    this.analyser.getByteTimeDomainData(this.timeBytes)

    const bins = this.freqBytes.length
    for (let i = 0; i < bins; i++) this.freqFloat[i] = this.freqBytes[i] / 255

    let rms = 0
    for (let i = 0; i < this.timeBytes.length; i++) {
      const v = (this.timeBytes[i] - 128) / 128
      this.waveFloat[i] = v
      rms += v * v
    }
    rms = Math.sqrt(rms / this.timeBytes.length)

    // Bands: we split the bins into bass / mid / treble.
    const bass = avg(this.freqFloat, 0, Math.floor(bins * 0.08))
    const mid = avg(this.freqFloat, Math.floor(bins * 0.08), Math.floor(bins * 0.4))
    const treble = avg(this.freqFloat, Math.floor(bins * 0.4), bins)

    // Beat detection from the BASS band with an adaptive threshold. Runs ONCE
    // per real frame (using real elapsed time) so multiple readers don't
    // double-advance the BPM clock — the result is shared via this.frameBeat.
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now()
    if (this.lastAnalyzeMs === 0 || now - this.lastAnalyzeMs > 4) {
      const adt = this.lastAnalyzeMs === 0 ? dt : Math.min(0.1, (now - this.lastAnalyzeMs) / 1000)
      this.lastAnalyzeMs = now
      this.bpmClock += adt
      this.bassEnergyAvg = this.bassEnergyAvg * 0.95 + bass * 0.05
      this.beatCooldown = Math.max(0, this.beatCooldown - adt)
      this.frameBeat = false
      if (bass > this.bassEnergyAvg * 1.35 && bass > 0.25 && this.beatCooldown === 0) {
        this.frameBeat = true
        this.beatIntensity = 1
        this.beatCooldown = 0.12 // avoids multiple triggers on a single hit
        this.trackBpm()
      }
      this.beatIntensity = Math.max(0, this.beatIntensity - adt * 3)
    }
    const beat = this.frameBeat

    return {
      frequencies: this.freqFloat,
      waveform: this.waveFloat,
      bass,
      mid,
      treble,
      level: rms,
      beat,
      beatIntensity: this.beatIntensity,
      bpm: this.bpm,
    }
  }

  /** Records a beat time and re-estimates the tempo from the median interval. */
  private trackBpm() {
    this.beatTimes.push(this.bpmClock)
    if (this.beatTimes.length > 16) this.beatTimes.shift()
    if (this.beatTimes.length < 5) return
    const intervals: number[] = []
    for (let i = 1; i < this.beatTimes.length; i++) {
      const d = this.beatTimes[i] - this.beatTimes[i - 1]
      if (d > 0.05) intervals.push(d) // ignore double-triggers
    }
    if (intervals.length < 3) return
    intervals.sort((a, b) => a - b)
    const median = intervals[Math.floor(intervals.length / 2)]
    if (median <= 0) return
    let bpm = 60 / median
    // Fold into a musical range so half/double-time reads land near the real tempo.
    while (bpm < 70) bpm *= 2
    while (bpm > 180) bpm /= 2
    this.bpm = this.bpm * 0.8 + bpm * 0.2 // smooth
  }

  get binCount() {
    return this.freqFloat.length
  }
}

function avg(arr: Float32Array, from: number, to: number): number {
  let s = 0
  const n = Math.max(1, to - from)
  for (let i = from; i < to; i++) s += arr[i]
  return s / n
}
